import { isDefined } from "@clipboard-health/util-ts";
import { apiClient } from "@src/appV2/api/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

const paymentAccountInfoSchema = z.object({
  accountId: z.string(),
  enabled: z.boolean(),
  status: z.string(),
});

const agentListItemSchema = z.object({
  agent: z
    .object({
      paymentAccountInfo: paymentAccountInfoSchema.optional(),
    })
    .optional(),
});

const agentListResponseSchema = z.object({
  agentList: z.array(agentListItemSchema),
});

export interface WorkerStripeAccount {
  accountId: string;
  stripeLink: string;
  enabled: boolean;
  status: string;
}

interface UseGetWorkerStripeAccountParams {
  workerId: string | undefined;
  enabled?: boolean;
}

export interface UseGetWorkerStripeAccountResult {
  data: WorkerStripeAccount | undefined;
  isLoading: boolean;
  hasStripeAccount: boolean;
}

export function getStripeAccountLink(accountId: string): string {
  const baseAccountId = "acct_1HrTYbCZ2LZg728W";
  const isProduction = environmentConfig.REACT_APP_ENVIRONMENT_NAME === "production";
  return isProduction
    ? `https://dashboard.stripe.com/${baseAccountId}/connect/accounts/${accountId}/activity`
    : `https://dashboard.stripe.com/${baseAccountId}/test/connect/accounts/${accountId}/activity`;
}

export function useGetWorkerStripeAccount(
  params: UseGetWorkerStripeAccountParams
): UseGetWorkerStripeAccountResult {
  const { workerId, enabled = true } = params;

  const isQueryEnabled = enabled && isDefined(workerId);

  const query = useQuery({
    queryKey: ["workerStripeAccount", workerId],
    queryFn: async () => {
      const response = await apiClient.get("/user/agentList", {
        params: { userIds: [workerId] },
      });
      return agentListResponseSchema.parse(response.data);
    },
    enabled: isQueryEnabled,
    staleTime: 30_000,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_WORKER_STRIPE_ACCOUNT_FAILURE,
    },
  });

  const paymentAccountInfo = query.data?.agentList[0]?.agent?.paymentAccountInfo;

  const stripeAccount: WorkerStripeAccount | undefined =
    isDefined(paymentAccountInfo) && isDefined(paymentAccountInfo.accountId)
      ? {
          accountId: paymentAccountInfo.accountId,
          stripeLink: getStripeAccountLink(paymentAccountInfo.accountId),
          enabled: paymentAccountInfo.enabled,
          status: paymentAccountInfo.status,
        }
      : undefined;

  return {
    data: stripeAccount,
    isLoading: query.isLoading,
    hasStripeAccount: isDefined(stripeAccount),
  };
}
